import { useState, useMemo, type FormEvent } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ArrowLeft, ShoppingCart, Check } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PackageInfo {
  daily_cost?: number
  platform: number
  printing?: number
  limit?: number
}

interface Props extends Omit<AppShellProps, 'children'> {
  event: { id: number; name: string }
  package: PackageInfo
  urls: AppShellProps['urls'] & {
    createOrder: string
  }
  translations: AppShellProps['translations'] & {
    orderDetails: string
    description: string
    price: string
    continueToCheckout: string
    additionalOnsiteServices: string
    serviceFee: string
    perBadge: string
    perDay: string
    howManyDays: string
    total: string
    landingPage: string
    upTo: string
    registration: string
    autoBadge: string
    autoQr: string
    attendanceReports: string
    packageFor: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function New(props: Props) {
  const { event, package: pkg, urls, translations: t, ...shell } = props
  const { flash } = usePage<{ flash: { notice?: string; alert?: string } }>().props

  const [additional, setAdditional] = useState(false)
  const [eventDays, setEventDays] = useState(1)

  const additionalCost = useMemo(() => {
    if (!additional || !pkg.daily_cost || !pkg.printing || !pkg.limit) return 0
    return eventDays * pkg.daily_cost + pkg.printing * pkg.limit
  }, [additional, eventDays, pkg])

  const grandTotal = useMemo(() => {
    return pkg.platform + additionalCost
  }, [pkg.platform, additionalCost])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    router.post(urls.createOrder, {
      order: {
        event_id: event.id,
        event_days: additional ? eventDays : 0,
        package: new URLSearchParams(window.location.search).get('package') || '1',
        total: grandTotal,
      },
    })
  }

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={t.orderDetails} />

      <div className="mx-auto max-w-3xl space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => window.history.back()}
            className="text-[#91918E] hover:bg-[#F1F1EF]"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-semibold text-[#37352F]">{t.orderDetails}</h1>
        </div>

        {/* Flash messages */}
        {flash?.alert && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {flash.alert}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <Card className="border-[#E8E8E5]">
            <CardContent className="p-0">
              {/* Table header */}
              <div className="grid grid-cols-[1fr_auto] border-b border-[#E8E8E5] bg-[#F1F1EF] px-6 py-3">
                <span className="text-sm font-medium text-[#37352F]">{t.description}</span>
                <span className="w-32 text-center text-sm font-medium text-[#37352F]">{t.price}</span>
              </div>

              {/* Package row */}
              <div className="grid grid-cols-[1fr_auto] border-b border-[#E8E8E5] px-6 py-4">
                <div>
                  <h3 className="font-semibold text-[#37352F]">
                    {t.packageFor} {pkg.limit ?? 'Unlimited'} {t.registration}
                  </h3>
                  <ul className="mt-2 space-y-1 text-sm text-[#91918E]">
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-[#2383E2]" />
                      {t.landingPage}
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-[#2383E2]" />
                      {t.upTo} {pkg.limit ?? 'Unlimited'} {t.registration}
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-[#2383E2]" />
                      {t.autoBadge}
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-[#2383E2]" />
                      {t.autoQr}
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-[#2383E2]" />
                      {t.attendanceReports}
                    </li>
                  </ul>
                </div>
                <div className="flex w-32 items-center justify-center">
                  <span className="text-lg font-bold text-[#37352F]">{pkg.platform} SAR</span>
                </div>
              </div>

              {/* Additional services row */}
              {pkg.daily_cost && pkg.printing && (
                <div className="grid grid-cols-[1fr_auto] border-b border-[#E8E8E5] px-6 py-4">
                  <div>
                    <label className="flex cursor-pointer items-center gap-3">
                      <input
                        type="checkbox"
                        checked={additional}
                        onChange={(e) => setAdditional(e.target.checked)}
                        className="h-4 w-4 rounded border-[#E8E8E5] text-[#2383E2] focus:ring-[#2383E2]"
                      />
                      <span className="font-semibold text-[#37352F]">
                        {t.additionalOnsiteServices}
                      </span>
                    </label>

                    <div className="ml-7 mt-2 space-y-2 text-sm text-[#91918E]">
                      <p>{pkg.printing} SAR {t.perBadge}</p>
                      <p>{t.serviceFee || 'Service Fee'} {pkg.daily_cost} {t.perDay}</p>

                      <div className="mt-3">
                        <label className="block text-sm text-[#37352F]">{t.howManyDays}</label>
                        <select
                          disabled={!additional}
                          value={eventDays}
                          onChange={(e) => setEventDays(Number(e.target.value))}
                          className="mt-1 rounded-md border border-[#E8E8E5] bg-white px-3 py-2 text-sm text-[#37352F] focus:border-[#2383E2] focus:outline-none focus:ring-1 focus:ring-[#2383E2] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="flex w-32 items-center justify-center">
                    <span className="text-lg font-bold text-[#37352F]">
                      {additionalCost > 0 ? `${additionalCost} SAR` : '0'}
                    </span>
                  </div>
                </div>
              )}

              {/* Total row */}
              <div className="grid grid-cols-[1fr_auto] bg-[#F1F1EF] px-6 py-4">
                <span className="text-base font-semibold text-[#37352F]">{t.total}</span>
                <span className="w-32 text-center text-lg font-bold text-[#2383E2]">
                  {grandTotal} SAR
                </span>
              </div>
            </CardContent>
          </Card>

          <div className="mt-6 flex justify-end">
            <Button
              type="submit"
              className="bg-[#2383E2] px-6 text-white hover:bg-[#1b6ec2]"
            >
              <ShoppingCart className="mr-2 h-4 w-4" />
              {t.continueToCheckout}
            </Button>
          </div>
        </form>
      </div>
    </AppShell>
  )
}
